import React from 'react';
import { Copy, Check, Plus } from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';
import { getEditionTypeInfo, getStatusBadge } from './editionTreeUtils.js';

interface EditionCompareViewProps {
  editions: Book[];
  currentBookId: string;
  selectedNode: Book;
  onSelectNode: (book: Book) => void;
  copiedUibn: string | null;
  onCopyUibn: (uibn: string, e?: React.MouseEvent) => void;
  onOpenBookDetail?: (book: Book) => void;
  onRequestCreateEdition?: (parentBook: Book) => void;
}

export const EditionCompareView: React.FC<EditionCompareViewProps> = ({
  editions,
  currentBookId,
  selectedNode,
  onSelectNode,
  copiedUibn,
  onCopyUibn,
  onOpenBookDetail,
  onRequestCreateEdition,
}) => {
  return (
    <div className="space-y-3">
      {/* Mobile-Friendly Comparison Cards (Shown on mobile screens < md) */}
      <div className="block md:hidden space-y-3">
        <div className="text-xs text-slate-500 font-medium px-1">
          Menampilkan perbandingan komparatif {(editions || []).length} edisi buku:
        </div>

        {(editions || []).map((b, idx) => {
          const isCurrent = b.id === currentBookId;
          const isSelected = selectedNode.id === b.id;
          const typeInfo = getEditionTypeInfo(b.editionType);

          return (
            <div
              key={`compare-card-${b.id}-${idx}`}
              onClick={() => onSelectNode(b)}
              className={`bg-white rounded-xl border p-3.5 transition shadow-xs cursor-pointer ${
                isSelected
                  ? 'border-indigo-500 ring-2 ring-indigo-300/70 shadow-sm'
                  : isCurrent
                  ? 'border-indigo-300 bg-indigo-50/20'
                  : 'border-slate-200'
              }`}
            >
              {/* Card Header: Edition, Type, Status */}
              <div className="flex items-center justify-between gap-1.5 mb-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-bold text-slate-900 text-xs sm:text-sm">
                    {b.edition || `Edisi ${idx + 1}`}
                  </span>
                  {isCurrent && (
                    <span className="text-[10px] bg-indigo-600 text-white font-bold px-1.5 py-0.2 rounded">
                      Aktif
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${typeInfo.badge}`}>
                    {typeInfo.label}
                  </span>
                </div>
                {getStatusBadge(b.status)}
              </div>

              {/* Book Title */}
              <h4 className="text-xs sm:text-sm font-bold text-slate-900 mb-2 leading-snug">
                {b.title}
              </h4>

              {/* UIBN copyable */}
              <div className="bg-slate-50 rounded-lg p-2 flex items-center justify-between border border-slate-100 mb-2.5">
                <span className="font-mono text-xs font-semibold text-slate-700 truncate">
                  {b.uibn}
                </span>
                {b.status !== 'diajukan' && (
                  <button
                    type="button"
                    onClick={(e) => onCopyUibn(b.uibn, e)}
                    className="text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-200 transition cursor-pointer"
                  >
                    {copiedUibn === b.uibn ? (
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                )}
              </div>

              {/* Key Attributes Micro-grid */}
              <div className="grid grid-cols-3 gap-2 bg-slate-50/80 p-2 rounded-lg border border-slate-100 text-[11px] mb-2.5">
                <div>
                  <span className="text-slate-400 block text-[10px]">Tahun</span>
                  <span className="font-semibold text-slate-800">{b.publicationYear}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">Halaman</span>
                  <span className="font-semibold text-slate-800">{b.pages || 0} hlm</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">Bahasa</span>
                  <span className="font-semibold text-slate-800">{b.language || 'Indonesia'}</span>
                </div>
              </div>

              {/* Highly readable changelog / notes on mobile */}
              <div className="mb-3">
                <span className="text-[11px] font-bold text-slate-700 block mb-1">
                  Catatan Perubahan / Revisi:
                </span>
                {b.editionNotes ? (
                  <p className="text-xs text-slate-700 bg-amber-50/90 border border-amber-200/80 p-2.5 rounded-lg leading-relaxed italic">
                    "{b.editionNotes}"
                  </p>
                ) : (
                  <p className="text-xs text-slate-400 italic bg-slate-50 p-2 rounded-lg border border-slate-100">
                    Tidak ada catatan revisi khusus.
                  </p>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs">
                <span className="text-[11px] text-indigo-600 font-semibold">
                  {isSelected ? '✓ Terpilih di Detail' : 'Pilih Edisi Ini'}
                </span>
                <div className="flex items-center gap-1.5">
                  {onOpenBookDetail && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenBookDetail(b);
                      }}
                      className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded text-xs transition cursor-pointer"
                    >
                      Detail
                    </button>
                  )}
                  {onRequestCreateEdition && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRequestCreateEdition(b);
                      }}
                      className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 font-semibold rounded text-xs transition flex items-center gap-1 cursor-pointer"
                    >
                      <Plus className="w-3 h-3" /> Edisi Baru
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop Table Comparison View (Shown on screens >= md) */}
      <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold">
                <th className="py-3 px-4">Edisi & Format</th>
                <th className="py-3 px-4">Kode UIBN</th>
                <th className="py-3 px-4">Tahun</th>
                <th className="py-3 px-4">Halaman</th>
                <th className="py-3 px-4">Bahasa</th>
                <th className="py-3 px-4">ISBN Induk / Asli</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 min-w-[220px]">Catatan Perubahan / Revisi</th>
                <th className="py-3 px-4 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(editions || []).map((b, idx) => {
                const isCurrent = b.id === currentBookId;
                const isSelected = selectedNode.id === b.id;
                const typeInfo = getEditionTypeInfo(b.editionType);

                return (
                  <tr
                    key={`compare-tr-${b.id}-${idx}`}
                    onClick={() => onSelectNode(b)}
                    className={`transition cursor-pointer ${
                      isSelected
                        ? 'bg-indigo-50/90 font-medium ring-1 ring-inset ring-indigo-400'
                        : isCurrent
                        ? 'bg-slate-50/80 font-medium'
                        : 'hover:bg-slate-50/80'
                    }`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-900 flex items-center gap-1">
                          {b.edition || 'Edisi 1'}
                          {isCurrent && (
                            <span className="text-[10px] bg-indigo-600 text-white font-bold px-1.5 py-0.2 rounded">
                              Aktif
                            </span>
                          )}
                        </span>
                        <span
                          className={`inline-block mt-0.5 px-1.5 py-0.2 rounded text-[10px] font-semibold border ${typeInfo.badge} w-fit`}
                        >
                          {typeInfo.label}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono font-medium text-slate-700">
                      {b.uibn}
                    </td>
                    <td className="py-3 px-4 text-slate-700">{b.publicationYear}</td>
                    <td className="py-3 px-4 text-slate-700">{b.pages || 0} hlm</td>
                    <td className="py-3 px-4 text-slate-700">{b.language || 'Indonesia'}</td>
                    <td className="py-3 px-4 font-mono text-slate-500">
                      {b.isbnOriginal || '-'}
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(b.status)}</td>
                    <td className="py-3 px-4 text-slate-700 text-xs">
                      {b.editionNotes ? (
                        <span className="bg-amber-50 text-amber-900 px-2 py-1 rounded border border-amber-200/80 block font-normal italic">
                          "{b.editionNotes}"
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {onOpenBookDetail && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenBookDetail(b);
                            }}
                            className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded text-[11px] transition cursor-pointer"
                          >
                            Lihat
                          </button>
                        )}
                        {onRequestCreateEdition && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRequestCreateEdition(b);
                            }}
                            className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 font-semibold rounded text-[11px] transition flex items-center gap-1 cursor-pointer"
                          >
                            <Plus className="w-3 h-3" /> Edisi Baru
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
